import type { DiamondStyle } from "../../shared/api-contract";

/**
 * Ring Builder option-tile glyphs.
 *
 * ONE rule holds this file together: **every icon is drawn from the very data
 * its tile applies** — the partition count, the pattern's ratios, the catalog
 * entry's stone count and spread, the profile's own point list. Nothing here
 * is a hand-drawn picture of what an option is supposed to look like, so an
 * icon cannot drift from what pressing it does. (Same precedent as
 * `FreeStoneEditor`'s `PatternThumb`, which plots the positions
 * `applyFreePattern` will actually stamp.)
 */

const STROKE = { fill: "none", stroke: "currentColor", strokeWidth: 1.3, strokeLinecap: "round" as const };

// ─── Partition ───────────────────────────────────────────────────────────────

/**
 * The band seen face-on, cut into one arc per partition.
 *
 * `ratios` IS the split: pass the live ratios for the selected count and the
 * even ones for the rest. Even is not a guess — `setPartitionCount(n)` picks
 * the first catalog pattern with `n` ratios, and every one of those (2t-1:1,
 * 3t-1:1:1, 4t-1:1:1:1, 5t-1:1:1:1:1 and their Segment twins) is even, as is
 * the synthesised `nN-even` past five.
 *
 * `horizontal` splits ACROSS the band width instead of around it — the same
 * distinction `SplitPattern.direction` makes, read off the pattern id.
 */
export function PartitionIcon({
  ratios,
  horizontal = false,
  size = 26,
}: {
  ratios: readonly number[];
  horizontal?: boolean;
  size?: number;
}) {
  const total = ratios.reduce((a, b) => a + b, 0) || 1;
  const R = 9.2;
  const r = 5.2;

  if (ratios.length < 2) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
        <circle cx="12" cy="12" r={R} />
        <circle cx="12" cy="12" r={r} />
      </svg>
    );
  }

  if (horizontal) {
    // Concentric rings: the seams run along the circumference, so face-on they
    // read as bands nested between the outer and inner edge.
    let acc = 0;
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
        <circle cx="12" cy="12" r={R} />
        <circle cx="12" cy="12" r={r} />
        {ratios.slice(0, -1).map((w, i) => {
          acc += w / total;
          return <circle key={i} cx="12" cy="12" r={r + (R - r) * (1 - acc)} strokeWidth={0.9} />;
        })}
      </svg>
    );
  }

  // Angular: one radial seam per boundary, at the cumulative ratio angle.
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <circle cx="12" cy="12" r={R} />
      <circle cx="12" cy="12" r={r} />
      {ratios.slice(0, -1).map((w, i) => {
        acc += w / total;
        // −90° so the first seam sits at 12 o'clock, where the geometry starts.
        const a = acc * Math.PI * 2 - Math.PI / 2;
        return (
          <line
            key={i}
            x1={12 + Math.cos(a) * r}
            y1={12 + Math.sin(a) * r}
            x2={12 + Math.cos(a) * R}
            y2={12 + Math.sin(a) * R}
            strokeWidth={0.9}
          />
        );
      })}
    </svg>
  );
}

// ─── Balance ─────────────────────────────────────────────────────────────────

/**
 * A split pattern's ratios as a stacked bar — the "Balance" tile.
 *
 * Angular patterns stack along the band (left→right); Segment patterns stack
 * across its width (top→bottom), which is exactly the axis their seams run on.
 */
export function BalanceBars({
  ratios,
  horizontal = false,
  size = 30,
}: {
  ratios: readonly number[];
  horizontal?: boolean;
  size?: number;
}) {
  const total = ratios.reduce((a, b) => a + b, 0) || 1;
  const L = 26;
  const T = 11;
  let acc = 0;
  const cells = ratios.map((w) => {
    const start = acc;
    acc += w / total;
    return { start: start * L, len: (w / total) * L };
  });

  return (
    <svg
      width={size}
      height={size * (horizontal ? 1 : T / L)}
      viewBox={horizontal ? `0 0 ${T} ${L}` : `0 0 ${L} ${T}`}
      aria-hidden="true"
      className="text-primary"
    >
      {cells.map((c, i) => (
        <rect
          key={i}
          x={horizontal ? 0.6 : c.start + 0.3}
          y={horizontal ? c.start + 0.3 : 0.6}
          width={horizontal ? T - 1.2 : Math.max(0.6, c.len - 0.6)}
          height={horizontal ? Math.max(0.6, c.len - 0.6) : T - 1.2}
          rx={1.4}
          fill="currentColor"
          // Alternating opacity is the only way two adjacent metals read as two
          // metals in a monochrome glyph.
          opacity={i % 2 ? 0.34 : 0.85}
        />
      ))}
    </svg>
  );
}

// ─── Setting Type ────────────────────────────────────────────────────────────

/** Cut a channel for the run to sit in (`DIAMOND_STYLE_INFO.needsGroove`). */
const RAILED = new Set<DiamondStyle>(["channel", "canal-across", "side-channel", "clamp", "clamp-angled", "tension-1", "tension-2"]);
/** Runs a row ACROSS the band width at one angle, not around the ring. */
const ACROSS = new Set<DiamondStyle>(["cross-bezel", "cross-channel", "cross-around"]);
/** Explicit or scattered positions rather than an even run. */
const SCATTERED = new Set<DiamondStyle>(["free-scatter", "free-place", "free-stones"]);

/**
 * Where a setting's stones land on the unrolled band, in −1…1 strip space
 * (x = around the ring, y = across the width).
 *
 * Derived from the two numbers the tile actually applies — `defaultCount` and
 * `defaultSpreadDeg` off the served catalog entry — plus the placement law the
 * engine documents for the id (`gemPlacement.ts`, mirrored by `traits()` in
 * GemsTab). Count 0 means "auto": the engine fills the span, so the glyph does
 * too.
 */
function stoneLayout(style: DiamondStyle, count: number, spreadDeg: number): { x: number; y: number }[] {
  if (style === "none") return [];

  if (ACROSS.has(style)) {
    // One angle, a row across the width — the count comes from widthMM at build
    // time (catalog says 0), so three reads as "a row".
    return [-0.55, 0, 0.55].map((y) => ({ x: 0, y }));
  }

  if (SCATTERED.has(style)) {
    const n = count || 9;
    return Array.from({ length: n }, (_, i) => ({
      // Golden angle around the ring, alternating across the width — the same
      // "everywhere, evenly, never in a line" shape the scatter produces.
      x: (((i * 0.381966) % 1) * 2 - 1) * 0.9,
      y: (((i * 0.618034) % 1) * 2 - 1) * 0.55,
    }));
  }

  const spread = Math.min(360, Math.abs(spreadDeg));
  // Auto (count 0): the engine fits stones to the span; ~one per 45°.
  const n = count || Math.max(1, Math.round(spread / 45));
  if (n === 1 || spread === 0) return [{ x: 0, y: 0 }];
  const half = (spread / 360) * 0.92;
  return Array.from({ length: n }, (_, i) => ({ x: -half + (2 * half * i) / (n - 1), y: 0 }));
}

/**
 * A setting tile: the unrolled band strip with the stones this style would put
 * on it, plus rails when the style carves a channel to seat them in.
 */
export function SettingIcon({
  style,
  count,
  spreadDeg,
  size = 34,
}: {
  style: DiamondStyle;
  count: number;
  spreadDeg: number;
  size?: number;
}) {
  const pts = stoneLayout(style, count, spreadDeg);
  const W = 30;
  const H = 15;
  const sx = (x: number) => W / 2 + (x * (W - 6)) / 2;
  const sy = (y: number) => H / 2 + (y * (H - 6)) / 2;
  const railed = RAILED.has(style);
  // Across-runs are seated by their own cross carve, so their rails run the
  // other way — vertical, at the single placement angle.
  const acrossRail = style === "cross-channel";

  return (
    <svg width={size} height={(size * H) / W} viewBox={`0 0 ${W} ${H}`} aria-hidden="true" className="text-primary">
      <rect
        x={0.7}
        y={0.7}
        width={W - 1.4}
        height={H - 1.4}
        rx={3}
        fill="none"
        stroke="currentColor"
        strokeWidth={0.9}
        opacity={0.45}
      />
      {railed ? (
        <>
          <line x1={2.5} y1={H / 2 - 2.9} x2={W - 2.5} y2={H / 2 - 2.9} stroke="currentColor" strokeWidth={0.8} opacity={0.5} />
          <line x1={2.5} y1={H / 2 + 2.9} x2={W - 2.5} y2={H / 2 + 2.9} stroke="currentColor" strokeWidth={0.8} opacity={0.5} />
        </>
      ) : null}
      {acrossRail ? (
        <>
          <line x1={W / 2 - 2.4} y1={2.4} x2={W / 2 - 2.4} y2={H - 2.4} stroke="currentColor" strokeWidth={0.8} opacity={0.5} />
          <line x1={W / 2 + 2.4} y1={2.4} x2={W / 2 + 2.4} y2={H - 2.4} stroke="currentColor" strokeWidth={0.8} opacity={0.5} />
        </>
      ) : null}
      {pts.length === 0 ? (
        <line x1={W / 2 - 4} y1={H / 2} x2={W / 2 + 4} y2={H / 2} stroke="currentColor" strokeWidth={1} opacity={0.45} />
      ) : null}
      {pts.map((p, i) => (
        <circle key={i} cx={sx(p.x)} cy={sy(p.y)} r={pts.length > 8 ? 1.05 : 1.5} fill="currentColor" />
      ))}
    </svg>
  );
}

// ─── Profile cross-section ───────────────────────────────────────────────────

/**
 * The current profile drawn at its TRUE aspect (width × thickness).
 *
 * `path` is `ProfileItem.iconPath`, which the viewer generates with
 * `pointsToIconPath(points)` from the very point list the lathe extrudes — so
 * this is the geometry's own outline, not a redrawing of it. That generator
 * normalises into a 60×40 box with an 8/6 pad, hence the tight viewBox below;
 * `preserveAspectRatio="none"` then stretches it back to the real mm aspect.
 */
export function ProfileSection({
  path,
  widthMM,
  thicknessMM,
  height = 52,
}: {
  path: string;
  widthMM: number;
  thicknessMM: number;
  height?: number;
}) {
  const aspect = Math.min(6, Math.max(0.25, widthMM / Math.max(0.01, thicknessMM)));
  return (
    <svg
      width={height * aspect}
      height={height}
      viewBox="8 6 44 28"
      preserveAspectRatio="none"
      aria-hidden="true"
      className="text-primary"
      style={{ maxWidth: "100%" }}
    >
      <path d={path} fill="currentColor" fillOpacity={0.1} stroke="currentColor" strokeWidth={1.2} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
